import { useEffect, useMemo } from "react";
import { ROSARY_SETS, ROSARY_PRAYERS, buildSteps } from "@/data/rosary";
import { localizeSet } from "@/data/rosaryLocale";
import { rosaryUI } from "@/data/rosaryLocale";
import { rosaryRoseForStep } from "@/data/rosaryRoses";
import { useRosarySession, updateRosary, rosaryWindowId } from "@/lib/rosarySession";

const MYSTERY_SEQUENCE = ["joyful", "luminous", "sorrowful", "glorious"];

export const useRosaryController = () => {
  const state = useRosarySession();
  const { setId, lang, stepIx, playingOwner } = state;
  const set = useMemo(() => localizeSet(ROSARY_SETS[setId], lang), [setId, lang]);
  const steps = useMemo(() => buildSteps(setId, lang), [setId, lang]);
  const prayedAnchors = useMemo(() => new Set(state.prayedAnchors), [state.prayedAnchors]);
  const step = steps[stepIx];
  const finished = stepIx === steps.length - 1;
  const nextSetId = MYSTERY_SEQUENCE[(MYSTERY_SEQUENCE.indexOf(setId) + 1) % MYSTERY_SEQUENCE.length];
  const nextSetLabel = `${rosaryUI(lang).continueTo} ${localizeSet(ROSARY_SETS[nextSetId], lang).short}`;
  const playing = playingOwner === rosaryWindowId && !finished;
  useEffect(() => () => updateRosary((current) => current.playingOwner === rosaryWindowId ? { ...current, playingOwner: null } : current), []);
  const navigate = (index) => updateRosary({ stepIx: Math.max(0, Math.min(index, steps.length - 1)), playingOwner: null });
  const advance = () => updateRosary((current) => {
    const prayerSteps = buildSteps(current.setId, current.lang);
    const prayer = prayerSteps[current.stepIx];
    return { ...current, stepIx: Math.min(current.stepIx + 1, prayerSteps.length - 1), playingOwner: null,
      prayedAnchors: rosaryRoseForStep(prayer) ? [...new Set([...current.prayedAnchors, prayer.anchor])] : current.prayedAnchors };
  });
  const continueToNextSet = () => updateRosary((current) => {
    if (current.stepIx !== buildSteps(current.setId, current.lang).length - 1) return current;
    const nextId = MYSTERY_SEQUENCE[(MYSTERY_SEQUENCE.indexOf(current.setId) + 1) % MYSTERY_SEQUENCE.length];
    return { ...current, setId: nextId, stepIx: 0, prayedAnchors: [], playingOwner: null };
  });
  useEffect(() => {
    if (!playing) return;
    const text = step.kind === "mystery" ? `${set.mysteries[step.index].verse} ${set.mysteries[step.index].meaning}` : ROSARY_PRAYERS[step.key || "hail_mary"][lang];
    const timer = setTimeout(() => updateRosary((current) => {
      if (current.playingOwner !== rosaryWindowId) return current;
      return { ...current, stepIx: Math.min(stepIx + 1, steps.length - 1), playingOwner: stepIx + 1 === steps.length - 1 ? null : rosaryWindowId,
        prayedAnchors: rosaryRoseForStep(step) ? [...new Set([...current.prayedAnchors, step.anchor])] : current.prayedAnchors };
    }), Math.max(8000, text.split(/\s+/).length * 60000 / 110));
    return () => clearTimeout(timer);
  }, [playing, stepIx, step, set, lang, steps.length]);
  return { setId, set, lang, steps, step, stepIx, prayedAnchors, finished, playing, nextSetId, nextSetLabel, continueToNextSet,
    currentMystery: step.index ?? (stepIx >= steps.length - 3 ? 4 : -1), navigate, advance,
    restart: () => updateRosary({ stepIx: 0, prayedAnchors: [], playingOwner: null }),
    chooseSet: (id) => updateRosary({ setId: id, stepIx: 0, prayedAnchors: [], playingOwner: null }),
    chooseLanguage: (value) => updateRosary({ lang: value, playingOwner: null }),
    jump: (index) => navigate(steps.findIndex((item) => item.kind === "mystery" && item.index === index)),
    togglePlaying: () => updateRosary({ playingOwner: playing ? null : rosaryWindowId }),
    pause: () => updateRosary({ playingOwner: null }),
  };
};